import React, { useContext, useState } from "react";
import intl from "react-intl-universal";
import { mdiClose, mdiContentSave } from "@mdi/js";
import styled from "styled-components";
import commonUtils from "../../../script/utils/commonUtils";

import userRest from "../../../rest/userRest";
import companyRest from "../../../rest/companyRest";

import { Grid } from "../../components/Grid";
import { useInput } from "../../hooks/useInput";
import { Dialog } from "../../containers/Dialog";
import { Button } from "../../components/Button";
import { Spacer } from "../../components/Spacer";
import { InputText } from "../../components/InputText";
import { DialogBody } from "../../containers/DialogBody";
import { useValidation } from "../../hooks/useValidation";
import { DialogFooter } from "../../containers/DialogFooter";
import { SelectSingle } from "../../components/SelectSingle";
import { ValidationContext } from "../../contexts/ValidationContext";

const DialogBodyStyle = styled.div`
    min-height: 250px;
`;

export const UserCreateDialog = props => {

    const validationContext = useContext(ValidationContext);

    const companyName = useInput("company", "");
    const username = useInput("username", "");
    const email = useInput("email", "");

    const [saving, setSaving] = useState(false);

    useValidation(value => {
        if (commonUtils.isBlank(value)) {
            return intl.get("user.error.companyName.presence");
        }
        return true;
    }, [companyName]);

    useValidation(async value => {
        if (commonUtils.isBlank(value)) {
            return intl.get("user.error.username.presence");
        }
        const isUsed = await userRest.isUsernameUsed(value);
        if (isUsed) {
            return intl.get("user.error.username.used");
        }
        const usernameRegex = /^[a-z-]+$/;
        if (!usernameRegex.test(value)) {
            return intl.get("user.error.username.format");
        }
        return true;
    }, [username]);

    useValidation(value => {
        if (commonUtils.isBlank(value)) {
            return intl.get("user.error.email.presence");
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            return intl.get("user.error.email.format");
        }
        return true;
    }, [email]);

    const onCreate = async () => {
        setSaving(true);
        const allValid = await validationContext.allValid();
        validationContext.setErrorsVisible(true);
        if (allValid) {
            await userRest.create(username.value, email.value, companyName.value);
            props.onClose();
        }
        setSaving(false);
    };

    return (
        <Dialog
            width="430px"
            title={intl.get("users.create")}
            onClose={() => props.onClose()}>
            <DialogBody>
                <DialogBodyStyle>
                    <Grid columns="1">
                        <SelectSingle {...companyName.bind}
                            disabled={saving}
                            title={intl.get("users.company")}>
                            {(async () => {
                                const companies = await companyRest.getAll();
                                return companies.map(company =>
                                    <option key={company.id} value={company.name}>{company.name}</option>
                                );
                            })}
                        </SelectSingle>
                        <InputText {...username.bind}
                            disabled={saving}
                            maxLength="30"
                            transform={value => value?.toLowerCase() ?? value}
                            title={intl.get("users.username")} />
                        <InputText {...email.bind}
                            disabled={saving}
                            maxLength="70"
                            transform={value => value?.toLowerCase() ?? value}
                            title={intl.get("users.email")} />
                    </Grid>
                </DialogBodyStyle>
            </DialogBody>
            <DialogFooter>
                <Button
                    svgIcon={mdiClose}
                    onClick={() => props.onClose()}
                    disabled={saving}
                    value={intl.get("common.cancel")} />
                <Spacer variant="vertical" />
                <Button
                    svgIcon={mdiContentSave}
                    disabled={saving}
                    onClick={onCreate}
                    value={intl.get("common.save")} />
            </DialogFooter>
        </Dialog>
    );
};